import { useState, useEffect, useRef } from 'react';

// WMO weather interpretation codes → emoji
// NB: every emoji ends with the U+FE0F "variation selector" so Android/iOS
// render the COLOR version (without it, ⛅🌦🌧🌨🌫⛈🌤 fall back to a
// black-and-white outline glyph).
const WMO_EMOJI = {
  0:  '☀️',                              // Clear sky
  1:  '🌤️', 2: '⛅️',  3: '⛅️',        // Mainly clear → overcast (use the vivid sun+cloud for all cloudy states, same colourful family)
  45: '🌫️', 48: '🌫️',                  // Fog
  51: '🌦️', 53: '🌦️', 55: '🌦️',      // Drizzle
  56: '🌦️', 57: '🌦️',                  // Freezing drizzle
  61: '🌧️', 63: '🌧️', 65: '🌧️',      // Rain
  66: '🌧️', 67: '🌧️',                  // Freezing rain
  71: '🌨️', 73: '🌨️', 75: '❄️',       // Snow
  77: '🌨️',                             // Snow grains
  80: '🌦️', 81: '🌧️', 82: '🌧️',      // Rain showers
  85: '🌨️', 86: '❄️',                  // Snow showers
  95: '⛈️',                              // Thunderstorm
  96: '⛈️', 99: '⛈️',                   // Thunderstorm + hail
};
function wmoEmoji(code) { return WMO_EMOJI[Number(code)] ?? '🌡️'; }

const STORAGE_KEY       = 'weather_permission'; // 'granted' | 'denied' | (absent → 'unknown')
const WEATHER_CACHE_KEY = 'weather_cache';
const COORDS_CACHE_KEY  = 'weather_coords';
const WEATHER_CACHE_TTL = 30 * 60 * 1000; // 30 min (fresh)

/**
 * Read weather cache.
 * allowStale = true → return data even if older than TTL (last-resort fallback).
 */
function readWeatherCache(allowStale = false) {
  try {
    const raw = localStorage.getItem(WEATHER_CACHE_KEY);
    if (!raw) return null;
    const { data, ts } = JSON.parse(raw);
    if (allowStale || Date.now() - ts < WEATHER_CACHE_TTL) return data;
    return null;
  } catch { return null; }
}

function writeWeatherCache(data) {
  try {
    localStorage.setItem(WEATHER_CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
  } catch {}
}

function readCoordsCache() {
  try {
    const raw = localStorage.getItem(COORDS_CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw); // { latitude, longitude }
  } catch { return null; }
}

/**
 * Blurs a GPS fix down to the neighbourhood.
 *
 * The phone hands over your position to about ten metres — which for a weather
 * forecast is absurd, and for a Play Store data declaration is the difference
 * between admitting to "precise location" and "approximate location". One
 * decimal place is roughly 11 km by 8 km at our latitudes: far coarser than the
 * 3 km² line Google draws, and still finer than the weather model itself, whose
 * grid squares are about 11 km wide. The forecast is identical; what leaves the
 * phone is a region instead of a doorstep.
 *
 * Applied at both doors — the cache and the request — so nothing downstream,
 * and nothing left in localStorage by an older version, keeps a fine fix.
 */
const coarse = n => Math.round(Number(n) * 10) / 10;

function writeCoordsCache(latitude, longitude) {
  try {
    localStorage.setItem(COORDS_CACHE_KEY, JSON.stringify({
      latitude: coarse(latitude), longitude: coarse(longitude),
    }));
  } catch {}
}

// Custom in-app permission prompt strings (shown BEFORE browser dialog)
export const WEATHER_PROMPT_STRINGS = {
  it:      { q: 'Consentire le previsioni meteo tramite accesso alla posizione?', allow: 'Consenti',  deny: 'No grazie'     },
  en:      { q: 'Allow weather forecast through location access?',                allow: 'Allow',     deny: 'No thanks'     },
  de:      { q: 'Wettervorhersage über Standortzugriff erlauben?',               allow: 'Erlauben',  deny: 'Nein danke'    },
  fr:      { q: "Autoriser la météo via l'accès à la localisation ?",            allow: 'Autoriser', deny: 'Non merci'     },
  nl:      { q: 'Weerbericht toestaan via locatietoegang?',                      allow: 'Toestaan',  deny: 'Nee bedankt'   },
  es:      { q: '¿Permitir previsiones del tiempo mediante acceso a la ubicación?', allow: 'Permitir', deny: 'No gracias'  },
  pt:      { q: 'Permitir previsão do tempo através do acesso à localização?',   allow: 'Permitir',  deny: 'Não obrigado'  },
  'pt-BR': { q: 'Permitir previsão do tempo usando sua localização?',            allow: 'Permitir',  deny: 'Não, obrigado' },
  ru:      { q: 'Разрешить прогноз погоды через доступ к местоположению?',      allow: 'Разрешить', deny: 'Нет, спасибо'  },
  pl:      { q: 'Zezwolić na prognozę pogody przez dostęp do lokalizacji?',     allow: 'Zezwól',    deny: 'Nie, dziękuję' },
  ro:      { q: 'Permiteți prognoza meteo prin acces la locație?',               allow: 'Permite',   deny: 'Nu, mulțumesc' },
  zh:      { q: '允许通过位置信息获取天气预报吗？',                                  allow: '允许',       deny: '不用了'         },
  ja:      { q: '位置情報を使って天気予報を表示しますか？',                            allow: '許可',       deny: 'いいえ'         },
  hi:      { q: 'स्थान की पहुँच से मौसम पूर्वानुमान की अनुमति दें?',                     allow: 'अनुमति दें',   deny: 'नहीं, धन्यवाद'   },
  ko:      { q: '위치 접근으로 일기예보를 허용할까요?',                                allow: '허용',       deny: '아니요'         },
  ar:      { q: 'السماح بتوقعات الطقس عبر الوصول إلى الموقع؟',                     allow: 'السماح',     deny: 'لا شكرًا'       },
};

/**
 * useWeather — fetches a forecast from Open-Meteo (free, no API key).
 *
 * Reliability strategy (definitive fix):
 *
 * Problem 1 — GPS latency: the old code waited up to 10 s for a GPS fix before
 *   even trying cached coordinates. On slow/indoor GPS this meant 10 s of spinner
 *   or blank weather.
 * Fix: if we have cached coords, start the API fetch IMMEDIATELY. GPS runs in
 *   background only to refresh the coords cache for next time.
 *
 * Problem 2 — API failure shows blank: if both fetch attempts failed we set
 *   weatherLoading=false and weather stayed null, even when we had slightly
 *   stale but perfectly valid cached data.
 * Fix: on final failure, fall back to stale cache (allowStale=true) so the
 *   user always sees something rather than a blank slot.
 */
export function useWeather(useFahrenheit = false) {
  const [weather, setWeather]                 = useState(() => readWeatherCache(true)); // show stale on init too
  const [permissionState, setPermissionState] = useState(
    () => localStorage.getItem(STORAGE_KEY) ?? 'unknown'
  );
  const [weatherLoading, setWeatherLoading]   = useState(
    () => localStorage.getItem(STORAGE_KEY) === 'granted' && !readWeatherCache(true)
  );
  const fetchedRef = useRef(false);

  function doFetch(lat0, lon0, isRetry = false) {
    if (fetchedRef.current) return;

    // Blur here too, not only on the way into the cache: a phone that used an
    // older version still has a fine fix stored, and it would otherwise travel
    // out on the very first request after the update.
    const latitude  = coarse(lat0);
    const longitude = coarse(lon0);

    writeCoordsCache(latitude, longitude);
    setWeatherLoading(true);

    const pad = n => String(n).padStart(2, '0');
    const toDateStr = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    const now       = new Date();
    // Reach back a full week so the week view always covers Mon–Sun of the
    // current week, even when today falls late in the week (e.g. Saturday).
    const startDate = toDateStr(new Date(now.getTime() - 8 * 86400000));
    const endDate   = toDateStr(new Date(now.getTime() + 14 * 86400000));

    const params = new URLSearchParams({
      // One decimal, deliberately: see coarse() above. Four decimals here used
      // to put an eleven-metre fix in the query string of every request.
      latitude:         latitude.toFixed(1),
      longitude:        longitude.toFixed(1),
      current:          'temperature_2m,weather_code',
      daily:            'weather_code,temperature_2m_max,temperature_2m_min',
      start_date:       startDate,
      end_date:         endDate,
      timezone:         'auto',
      temperature_unit: useFahrenheit ? 'fahrenheit' : 'celsius',
    });

    fetch(`https://api.open-meteo.com/v1/forecast?${params}`)
      .then(r => r.json())
      .then(data => {
        if (!data.daily?.time?.length) throw new Error('No daily data');
        const forecast = {};
        data.daily.time.forEach((date, i) => {
          const max     = data.daily.temperature_2m_max?.[i];
          const min     = data.daily.temperature_2m_min?.[i];
          const rawTemp = max != null ? max : (min != null ? min : null);
          forecast[date] = {
            emoji: wmoEmoji(data.daily.weather_code?.[i] ?? 0),
            temp:  rawTemp != null ? Math.round(rawTemp) : null,
          };
        });
        const curTemp = data.current?.temperature_2m;
        const weatherData = {
          current: {
            emoji: wmoEmoji(data.current?.weather_code ?? 0),
            temp:  curTemp != null ? Math.round(curTemp) : null,
          },
          forecast,
        };
        writeWeatherCache(weatherData);
        fetchedRef.current = true;
        setWeatherLoading(false);
        setWeather(weatherData);
      })
      .catch(() => {
        if (!isRetry) {
          // First failure — wait 4 s and retry once
          setTimeout(() => doFetch(latitude, longitude, true), 4000);
        } else {
          // Both attempts failed — show stale cache rather than blank
          const stale = readWeatherCache(true);
          if (stale) setWeather(stale);
          setWeatherLoading(false);
          // Allow a future retry (don't lock fetchedRef=true on failure)
        }
      });
  }

  /**
   * Core positioning strategy:
   *
   * 1. If we have cached coordinates → start the API fetch RIGHT NOW (no GPS wait).
   *    GPS runs in background only to update the coords cache for next session.
   *
   * 2. If no cached coords (first use) → must wait for GPS once.
   *    After success the coords are cached, so subsequent loads are instant.
   */
  function getPositionThenFetch(onDenied) {
    if (!navigator?.geolocation) { setWeatherLoading(false); return; }

    const cached = readCoordsCache();

    if (cached) {
      // ── Fast path: fetch immediately with known coords ──────────────
      doFetch(cached.latitude, cached.longitude);

      // Background GPS update — just refreshes the coords cache, no re-fetch
      navigator.geolocation.getCurrentPosition(
        ({ coords: { latitude, longitude } }) => writeCoordsCache(latitude, longitude),
        () => {}, // GPS failure is harmless — we already fetched
        { timeout: 10000, maximumAge: 300_000 }
      );
      return;
    }

    // ── Slow path: no cached coords yet — wait for GPS once ──────────
    navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude, longitude } }) => doFetch(latitude, longitude),
      (err) => {
        if (err.code === 1) {
          // User explicitly denied in browser
          onDenied?.();
          return;
        }
        // GPS unavailable/timeout and no coords cache — show retry button
        setWeatherLoading(false);
      },
      { timeout: 10000, maximumAge: 600_000 }
    );
  }

  // Auto-fetch on mount if the user already granted permission
  useEffect(() => {
    if (permissionState !== 'granted') return;
    getPositionThenFetch(() => {
      // Browser geolocation denied on auto-mount (e.g. user revoked location in browser settings
      // after previously allowing it in our app).
      // Strategy: keep permissionState = 'granted' so the retry button stays visible,
      // and show stale cache rather than a blank slot.
      // Only a full reset via retryWeather() clears everything.
      const stale = readWeatherCache(true);
      if (stale) setWeather(stale);
      setWeatherLoading(false);
    });
  }, []); // intentionally run once on mount

  /** Called when the user taps "Allow" on our custom in-app prompt. */
  function allow() {
    if (!navigator?.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords: { latitude, longitude } }) => {
        localStorage.setItem(STORAGE_KEY, 'granted');
        setPermissionState('granted');
        doFetch(latitude, longitude);
      },
      (err) => {
        if (err.code === 1) {
          localStorage.setItem(STORAGE_KEY, 'denied');
          setPermissionState('denied');
        } else {
          localStorage.setItem(STORAGE_KEY, 'granted');
          setPermissionState('granted');
          const cached = readCoordsCache();
          if (cached) doFetch(cached.latitude, cached.longitude);
          else setWeatherLoading(false);
        }
      },
      { timeout: 10000, maximumAge: 600_000 }
    );
  }

  function deny() {
    localStorage.setItem(STORAGE_KEY, 'denied');
    setPermissionState('denied');
  }

  function resetWeather() {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(WEATHER_CACHE_KEY);
    fetchedRef.current = false;
    setWeather(null);
    setWeatherLoading(false);
    setPermissionState('unknown');
  }

  function retryWeather() {
    fetchedRef.current = false;
    setWeatherLoading(true);
    getPositionThenFetch(() => {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(WEATHER_CACHE_KEY);
      setWeather(null);
      setWeatherLoading(false);
      setPermissionState('unknown');
    });
  }

  return { weather, weatherLoading, permissionState, allow, deny, retryWeather, resetWeather };
}
